import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { discountApi, type AffiliateStats } from '@/services/discountApi';
import type { AxiosError } from 'axios';
import { Login } from '../auth/Login';

interface AffiliateStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AffiliateStatsModal = ({ isOpen, onClose }: AffiliateStatsModalProps) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    void loadStats();
  }, [isOpen]);

  const loadStats = async () => {
    setLoading(true);
    setIsUnauthorized(false);
    try {
      const result = await discountApi.getAffiliateStats();
      setStats(result);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        setIsUnauthorized(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-xl border border-white/20">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="mb-2 text-xl font-semibold">
          {t('affiliate.stats.title', 'Referral Performance')}
        </h2>
        <p className="mb-6 text-sm text-muted-foreground">
          {t('affiliate.stats.description', 'Track registrations, printed items, and earnings from your referrals.')}
        </p>

        {loading ? (
          <div className="flex h-28 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : isUnauthorized ? (
          <Login standalone initialMode="login" onSuccess={() => { void loadStats(); }} />
        ) : stats ? (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border bg-muted/20 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t('affiliate.stats.totalRegistered', 'Total referred users')}
                </p>
                <p className="mt-2 text-2xl font-bold">{stats.totals.registeredUsers}</p>
              </div>
              <div className="rounded-lg border bg-muted/20 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t('affiliate.stats.totalPrinted', 'Total printed items')}
                </p>
                <p className="mt-2 text-2xl font-bold">{stats.totals.printedItems}</p>
              </div>
              <div className="rounded-lg border bg-muted/20 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t('affiliate.stats.totalEarned', 'Total money earned')}
                </p>
                <p className="mt-2 text-2xl font-bold">${stats.totals.moneyEarned.toFixed(2)}</p>
              </div>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">{t('affiliate.stats.table.date', 'Date')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('affiliate.stats.table.registered', 'Registered users')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('affiliate.stats.table.printed', 'Printed items')}</th>
                    <th className="px-3 py-2 text-left font-semibold">{t('affiliate.stats.table.earned', 'Money earned')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.rows.length > 0 ? (
                    stats.rows.map((row) => (
                      <tr key={row.date} className="border-t">
                        <td className="px-3 py-2">{new Date(row.date).toLocaleDateString()}</td>
                        <td className="px-3 py-2">{row.registeredUsers}</td>
                        <td className="px-3 py-2">{row.printedItems}</td>
                        <td className="px-3 py-2">${row.moneyEarned.toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-t">
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        {t('affiliate.stats.table.empty', 'No referral activity yet.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-muted-foreground">
              {t('affiliate.stats.error', 'Failed to load affiliate stats. Please try again.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
